import React, { createContext, useCallback, useContext, useState } from 'react';
import { UpgradePrompt } from '../components/Subscription/UpgradePrompt';
import type { UpgradeGate } from '../config/limits';

/**
 * Lets any gated action anywhere in the app open the shared UpgradePrompt modal:
 *
 *   const { open } = useUpgradePrompt();
 *   if (!canPlan) { open(FEATURE_GATES.strategy); return; }
 *
 * One modal instance lives at the provider; gated actions just call open(gate).
 * The gate carries the TIER as well as the message, so the modal can preselect
 * the plan that actually unlocks what was clicked.
 */
interface UpgradePromptContextValue {
  open: (gate: UpgradeGate) => void;
}

const UpgradePromptContext = createContext<UpgradePromptContextValue>({
  open: () => {},
});

export function UpgradePromptProvider({ children }: { children: React.ReactNode }) {
  const [gate, setGate] = useState<UpgradeGate | null>(null);

  const open = useCallback((g: UpgradeGate) => setGate(g), []);
  const close = useCallback(() => setGate(null), []);

  return (
    <UpgradePromptContext.Provider value={{ open }}>
      {children}
      {gate != null && <UpgradePrompt gate={gate} onClose={close} />}
    </UpgradePromptContext.Provider>
  );
}

export function useUpgradePrompt(): UpgradePromptContextValue {
  return useContext(UpgradePromptContext);
}
